import { execAdmin } from '@src/apis/reware/utils';
import { requirePartner } from '@src/providers/dataProvider/shared';

import type { CatalogTemplateRow, WizardState } from './types';

interface CatalogFacet {
  tag: string;
  name: string;
}

interface CatalogItemsResponse {
  catalogItems: {
    count: number;
    page: number;
    pageSize: number;
    rows: Array<{
      parentSku: string | null;
      sku_subcode: string | null;
      color: string | null;
      colorCode: string | null;
      sizeCode: string | null;
      catalogFacets: CatalogFacet[] | null;
      partner_category: string | null;
      price: number | null;
      yerdlePrice: number | null;
      title: string | null;
    }> | null;
  } | null;
}

interface CatalogItemsVariables {
  partnerId: number;
  page: number;
  pageSize: number;
  facets?: { department?: string, category?: string } | null;
  parentSku?: string | null;
}

const QUERY = `
  query getCatalogItemsForTemplate(
    $partnerId: Int!
    $page: Int
    $pageSize: Int
    $facets: FacetFiltersInputTypeAdmin
    $parentSku: String
  ) {
    catalogItems(
      partnerId: $partnerId
      page: $page
      pageSize: $pageSize
      facets: $facets
      parentSku: $parentSku
      availability: "ALL"
    ) {
      count
      page
      pageSize
      rows {
        parentSku
        sku_subcode
        color
        colorCode
        sizeCode
        catalogFacets { tag name }
        partner_category
        price
        yerdlePrice
        title
      }
    }
  }
`;

const PAGE_SIZE = 500;

type RawRow = NonNullable<NonNullable<CatalogItemsResponse['catalogItems']>['rows']>[number];

function facetValue(facets: CatalogFacet[] | null, tag: string): string | null {
  if (!facets) return null;
  return facets.find(f => f.tag === tag)?.name ?? null;
}

const toRow = (raw: RawRow): CatalogTemplateRow => ({
  parentSku: raw.parentSku,
  skuSubcode: raw.sku_subcode,
  color: raw.color,
  colorCode: raw.colorCode,
  sizeCode: raw.sizeCode,
  department: facetValue(raw.catalogFacets, 'department'),
  partnerCategory: raw.partner_category ?? facetValue(raw.catalogFacets, 'category'),
  price: raw.price,
  yerdlePrice: raw.yerdlePrice,
  title: raw.title,
});

/**
 * Fetches catalog items for template generation in batches.
 *
 * Scope filtering uses a hybrid approach:
 * - Single department/category: server-side via facets parameter
 * - Multiple departments/categories or parent SKUs: fetch all, filter client-side
 *   (the catalogItems API only accepts single string values for facet filters)
 */
export const fetchCatalogItemsForTemplate = async (
  state: WizardState,
): Promise<CatalogTemplateRow[]> => {
  const partner = requirePartner();
  const partnerId = partner.id;

  // Build query variables based on scope
  const buildVariables = (page: number): CatalogItemsVariables => {
    const vars: CatalogItemsVariables = { partnerId, page, pageSize: PAGE_SIZE };

    if (state.scopeMode === 'departments') {
      const facets: { department?: string, category?: string } = {};
      if (state.selectedDepartments.length === 1) {
        facets.department = state.selectedDepartments[0];
      }
      if (state.selectedCategories.length === 1 && state.selectedDepartments.length <= 1) {
        facets.category = state.selectedCategories[0];
      }
      if (facets.department || facets.category) {
        vars.facets = facets;
      }
    } else if (state.scopeMode === 'parentSkus' && getParentSkuList(state.parentSkuInput).length === 1) {
      vars.parentSku = getParentSkuList(state.parentSkuInput)[0];
    }

    return vars;
  };

  // Fetch first page to get total count
  const firstVars = buildVariables(1);
  const firstData = await execAdmin<CatalogItemsVariables, CatalogItemsResponse>(
    QUERY,
    { variables: firstVars },
  );

  const catalogItems = firstData.catalogItems;
  if (!catalogItems?.rows) {
    return [];
  }

  const allRows: CatalogTemplateRow[] = catalogItems.rows
    .filter((r): r is NonNullable<typeof r> => r != null)
    .map(toRow);

  // Fetch remaining pages
  const totalPages = Math.ceil((catalogItems.count ?? 0) / PAGE_SIZE);
  for (let page = 2; page <= totalPages; page += 1) {
    const vars = buildVariables(page);
    const data = await execAdmin<CatalogItemsVariables, CatalogItemsResponse>(
      QUERY,
      { variables: vars },
    );
    const rows = data.catalogItems?.rows;
    if (rows) {
      allRows.push(
        ...rows.filter((r): r is NonNullable<typeof r> => r != null).map(toRow),
      );
    }
  }

  // Apply client-side filtering for multi-value scopes
  return applyClientSideFilters(allRows, state);
};

function getParentSkuList(input: string): string[] {
  return input
    .split(/[,\n]+/)
    .map(s => s.trim())
    .filter(Boolean);
}

function applyClientSideFilters(rows: CatalogTemplateRow[], state: WizardState): CatalogTemplateRow[] {
  if (state.scopeMode === 'departments') {
    const depts = new Set(state.selectedDepartments);
    const cats = new Set(state.selectedCategories);
    return rows.filter((row) => {
      if (depts.size > 1 && !depts.has(row.department ?? '')) return false;
      if (cats.size > 1 && !cats.has(row.partnerCategory ?? '')) return false;
      return true;
    });
  }

  if (state.scopeMode === 'parentSkus') {
    const skus = new Set(getParentSkuList(state.parentSkuInput));
    if (skus.size > 1) {
      return rows.filter(row => skus.has(row.parentSku ?? ''));
    }
  }

  return rows;
}
